import { ReactComponent as MobileIcon } from 'assets/images/mobile.svg';
import { SignupSchema } from 'constants/ValidationSchema';
import { useFormik } from 'formik';
import {
  deleteAllTokens,
  deleteInviteToken,
  getAllInviteTokens,
  updateCurrentInviteTokenWithMessage,
} from 'helper/inviteTokenHelper';
import { useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import GoogleLogin from 'react-google-login';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import GoogleIcon from '../../assets/images/google.svg';
import FormInput from '../../components/FormInput';
import AuthLayout from '../../components/Layout/AuthLayout';
import {
  OtpDetails,
  OtpVerificationModal,
} from '../../components/common/OtpVerificationModal';
import { navLinks } from '../../constants/paths';
import { onLogin } from '../../redux/authSlice';
import {
  useGoogleMutation,
  useLoginMutation,
  useRegisterMutation,
} from '../../redux/services/auth';
import Tick from '../../assets/images/greencircle.png';
import { Loader } from 'components/common/Loader';
import { useTranslation } from 'react-i18next';
import './Register.scss';
import { RootStates } from 'redux/store';
import { Checkbox, FormControlLabel } from '@mui/material';
import { TermsModal } from 'components/common/TermsModal';

const Register = () => {
  const { t } = useTranslation();
  const [regsiter, { isLoading }] = useRegisterMutation();
  const [login, { isLoading: logingIn }] = useLoginMutation();
  const [displayTerms, setDisplayTerms] = useState(false);
  const [google] = useGoogleMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modal, setModal] = useState<{
    type: 'email' | 'mobile';
    value: string;
  } | null>(null);

  const inviteSource = useSelector((state: RootStates) => state.auth.source);
  const sourceEmail = useSelector(
    (state: RootStates) => state.auth.sourceEmail,
  );

  const formik = useFormik({
    initialValues: {
      name: '',
      email: sourceEmail,
      password: '',
      agreeTerms: true,
      emailVerified: inviteSource?.length > 0 ? true : false,
    },
    validationSchema: SignupSchema,
    onSubmit: async (values) => {
      if (!values.emailVerified) {
        return toast.error(t('please_verify_email'));
      }
      const inviteToken = localStorage.getItem('inviteToken');
      const res: any = await regsiter({
        body: values,
        inviteToken,
        source: inviteSource || '',
      });
      if (res.data) {
        if (res.data.inviteSuccess) {
          updateCurrentInviteTokenWithMessage(res.data.inviteMessage);
        }
        const loginRes: any = await login({
          body: {
            email: values.email,
            password: values.password,
          },
        });

        if (loginRes.data) {
          onLoginSuccess(loginRes.data);
        } else {
          toast.error(loginRes.error.data.message);
          navigate('/auth/login');
        }
      } else {
        toast.error(res.error.data.message);
      }
    },
  });

  const onLoginSuccess = (values: any) => {
    localStorage.setItem('c360labs/user-details', JSON.stringify(values));
    dispatch(onLogin());
    if (values.inviteMessage) {
      toast[values.inviteSuccess ? 'success' : 'error'](values.inviteMessage);
      deleteInviteToken();
    } else {
      const messages = getAllInviteTokens();
      messages.forEach((token) => {
        toast.success(token.message);
      });
      deleteAllTokens();
    }
    navigate('/onboarding');
  };

  const onSubmit = (response: any, provider: string = '') => {
    if (provider === 'google') {
      localStorage.setItem('c360labs/user-details', JSON.stringify(response));
      dispatch(onLogin());
      toast.success(t('login_success'));
      if (response.inviteMessage) {
        toast[response.inviteSuccess ? 'success' : 'error'](
          response.inviteMessage,
        );
        deleteInviteToken();
      }
      navigate('/onboarding');
    }
  };

  const responseGoogle = async (response: any) => {
    if (response) {
      const inviteToken = localStorage.getItem('inviteToken');
      const values = {
        email: response.profileObj.email,
        name: response.profileObj.name,
        imageUrl: response.profileObj.imageUrl,
      };
      const res: any = await google({
        body: values,
        inviteToken,
        source: inviteSource,
      });
      if (res.data) {
        onSubmit(res.data, 'google');
      } else toast.error(res.error.data.message);
    }
  };

  const handleFailure = (result: any) => {
    console.log(JSON.stringify(result));
  };

  const handleHide = () => setModal(null);
  const getSetModalHandler =
    (type: 'email' | 'mobile', value: string) => async () => {
      if (
        formik.getFieldMeta(type).error ||
        !String(formik.getFieldMeta(type).value).length
      ) {
        return toast.error(t('fill_in_correct_value', { type }));
      }
      setModal({ type, value });
    };

  const handleSubmitOtpVerification = (otpDetails: OtpDetails) => {
    formik.setFieldValue('emailVerified', otpDetails.verified);
    setModal(null);
  };

  const handleTermsClick = () => {
    setDisplayTerms(true);
  };

  const handleCloseTermsModal = () => {
    setDisplayTerms(false);
  };

  const { errors, touched } = formik;
  return (
    <AuthLayout>
      <div className="lgBox register justify-content-lg-center">
        {(isLoading || logingIn) && <Loader />}
        <h2>{t('register')}</h2>
        <p className="text-center tag-line">{t('login_branding')}</p>
        <form onSubmit={formik.handleSubmit} noValidate>
          <FormInput
            label={t('label_name')}
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            touched={touched.name}
            error={errors.name}
          />

          <div className="email-container d-flex mb-3 ">
            <FormInput
              label={t('your_registered_email')}
              type="email"
              name="email"
              className="email-form-input flex-grow-1"
              value={formik.values.email}
              onChange={formik.handleChange}
              touched={touched.email}
              disabled={
                formik.values.emailVerified && formik.values.email?.length > 0
              }
              error={errors.email}
            />
            {formik.values.emailVerified ? (
              formik.values.email?.length > 0 ? (
                <div className="entity-verified">
                  <img alt="" src={Tick} />
                  <span>{` (${t('verified')})`}</span>
                </div>
              ) : (
                ''
              )
            ) : (
              <Button
                onClick={getSetModalHandler('email', formik.values.email)}
              >
                {`${t('btn_label_otp_verify')} ->`}
              </Button>
            )}
          </div>

          <FormInput
            label={t('label_password')}
            type="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            touched={touched.password}
            error={errors.password}
          />
          <div className="form-group">
            <FormControlLabel
              control={<Checkbox checked={formik.values.agreeTerms} />}
              name="agreeTerms"
              label=""
              onChange={formik.handleChange}
            />
            <label className="form-check-label">
              {t('i_agree')}
              <span onClick={handleTermsClick} className="terms-link">
                {t('terms_and_conditions')}
              </span>
            </label>
          </div>
          <button
            type="submit"
            className="btn btn-primary w-100 mb-2 mt-2"
            disabled={isLoading || logingIn || !formik.values.agreeTerms}
          >
            {t('register')}
          </button>
        </form>
        <p className="text-center mb-2">{t('or')}</p>
        <Stack direction="horizontal" gap={3} className="mb-3">
          <GoogleLogin
            clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}
            render={(renderProps: any) => (
              <Button
                variant="outline-primary"
                className="d-flex align-items-center justify-content-center w-100"
                onClick={renderProps.onClick}
                disabled={renderProps.disabled || !formik.values.agreeTerms}
              >
                <img alt="" src={GoogleIcon} width="20" />
                <span className="ms-1">{t('google')}</span>
              </Button>
            )}
            onSuccess={responseGoogle}
            onFailure={handleFailure}
            cookiePolicy={'single_host_origin'}
          />
          <Link
            to={navLinks.mobile}
            className={`btn btn-outline-primary w-100 d-flex align-items-center justify-content-center ${
              !formik.values.agreeTerms ? 'disabled' : ''
            }`}
          >
            <MobileIcon width={18} />
            <span className="ms-1">{t('mobile')}</span>
          </Link>
        </Stack>

        <div className="form-group text-center">
          {t('already_have_account')}
          <Link to={navLinks.login}>{t('login')}</Link>
        </div>
        <OtpVerificationModal
          open={!!modal}
          value={modal?.value ?? ''}
          type={modal?.type ?? 'email'}
          onHide={handleHide}
          onSubmit={handleSubmitOtpVerification}
        />
        <TermsModal open={displayTerms} onClose={handleCloseTermsModal} />
      </div>
    </AuthLayout>
  );
};

export default Register;
